import { lazy } from "react";
import { RouteObject } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminProtectedRoute from "@/components/AdminProtectedRoute";
import { UserRole } from "@/lib/types";

// Eager imports — fast first load
import Index from "@/pages/Index";
import NotFound from "@/pages/NotFound";

// Lazy imports — code split by route
const Dashboard = lazy(() => import("@/pages/dashboard"));
const Cart = lazy(() => import("@/pages/cart"));
const Profile = lazy(() => import("@/pages/profile"));
const CreateProductWizard = lazy(() => import("@/pages/CreateProductWizard"));
const OrderConfirmation = lazy(() => import("@/pages/OrderConfirmation"));
const Orders = lazy(() => import("@/pages/Orders"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const ProductLandingPage = lazy(() => import("@/pages/ProductLandingPage"));
const ProductCatalog = lazy(() => import("@/pages/ProductCatalog"));
const ProductCatalogPublic = lazy(() => import("@/pages/ProductCatalogPublic"));
const ProductDetailsNew = lazy(() => import("@/pages/ProductDetailsNew"));
const AdminOrders = lazy(() => import("@/pages/AdminOrders"));
const AdminOrderDetails = lazy(() => import("@/pages/AdminOrderDetails"));
const AdminOrderSearch = lazy(() => import("@/pages/AdminOrderSearch"));
const AdminUserSearch = lazy(() => import("@/pages/AdminUserSearch"));
const SharedDesignPage = lazy(() => import("@/pages/SharedDesignPage"));
const AffiliateApply = lazy(() => import("@/pages/AffiliateApply"));
const AffiliateDashboard = lazy(() => import("@/pages/AffiliateDashboard"));
const AdminAffiliates = lazy(() => import("@/pages/AdminAffiliates"));
const AdminCoupons = lazy(() => import("@/pages/AdminCoupons"));
const Credits = lazy(() => import("@/pages/credits"));
const SentryExamplePage = lazy(() => import("@/pages/SentryExamplePage"));

export interface AppRoute extends Omit<RouteObject, "element"> {
  element: React.ReactElement;
  protected?: boolean;
  adminRequired?: boolean;
  requiredRoles?: UserRole[]; // Routes only visible to users with these roles
  title?: string;
  navbar?: boolean;
}

export const routes: AppRoute[] = [
  {
    path: "/",
    element: <Index />,
    title: "Home",
    navbar: false,
  },
  {
    path: "/projects/new",
    element: <CreateProductWizard />,
    protected: true,
    title: "New Design",
    navbar: false,
  },
  {
    path: "/dashboard",
    element: <Dashboard />,
    protected: true,
    title: "Dashboard",
    navbar: true,
  },
  {
    path: "/cart",
    protected: true,
    element: <Cart />,
    title: "Shopping Cart",
    navbar: true,
  },
  {
    path: "/privacy",
    element: <PrivacyPolicy />,
    title: "Privacy Policy",
    navbar: false,
  },
  {
    path: "/terms",
    element: <Terms />,
    title: "Terms of Service",
    navbar: false,
  },
  {
    path: "/products",
    element: <ProductCatalogPublic />,
    title: "Products",
    navbar: true,
    protected: false, // PUBLIC route
  },
  {
    path: "/products/:slug",
    element: <ProductLandingPage />,
    title: "Product Details",
    navbar: false,
    protected: false, // PUBLIC route
  },
  {
    path: "/designs/:projectId",
    element: <SharedDesignPage />,
    title: "Shared Design",
    navbar: false,
    protected: false, // PUBLIC route
  },
  {
    path: "/profile",
    element: <Profile />,
    protected: true,
    title: "Profile",
    navbar: true,
  },
  {
    path: "/credits",
    element: <Credits />,
    protected: true,
    title: "Buy Credits",
    navbar: false,
  },
  {
    path: "/order-confirmation",
    element: <OrderConfirmation />,
    protected: true,
    title: "Order Confirmation",
    navbar: false,
  },
  {
    path: "/orders",
    element: <Orders />,
    protected: true,
    title: "Orders",
    navbar: true,
  },
  // Affiliate routes
  {
    path: "/affiliate/apply",
    element: <AffiliateApply />,
    title: "Become an Affiliate",
    navbar: false,
  },
  {
    path: "/affiliate/dashboard",
    element: <AffiliateDashboard />,
    protected: true,
    requiredRoles: ["affiliate"],
    title: "Affiliate Dashboard",
    navbar: true,
  },
  // Admin routes
  {
    path: "/admin/affiliates",
    element: <AdminAffiliates />,
    protected: true,
    adminRequired: true,
    title: "Manage Affiliates",
    navbar: true,
  },
  {
    path: "/admin/coupons",
    element: <AdminCoupons />,
    protected: true,
    adminRequired: true,
    title: "Manage Coupons",
    navbar: true,
  },
  {
    path: "/admin/catalog",
    element: <ProductCatalog />,
    protected: true,
    adminRequired: true,
    title: "Manage Catalog",
    navbar: true,
  },
  {
    path: "/admin/catalog/:productId",
    element: <ProductDetailsNew />,
    protected: true,
    adminRequired: true,
    title: "Product Details",
    navbar: false,
  },
  {
    path: "/admin/orders",
    element: <AdminOrders />,
    protected: true,
    adminRequired: true,
    title: "Orders Overview",
    navbar: true,
  },
  {
    path: "/admin/orders/search",
    element: <AdminOrderSearch />,
    protected: true,
    adminRequired: true,
    title: "Search Orders",
    navbar: false,
  },
  {
    path: "/admin/orders/:orderId",
    element: <AdminOrderDetails />,
    protected: true,
    adminRequired: true,
    title: "Order Details",
    navbar: false,
  },
  {
    path: "/admin/users",
    element: <AdminUserSearch />,
    protected: true,
    adminRequired: true,
    title: "User Management",
    navbar: true,
  },
  {
    path: "/sentry-example-page",
    element: <SentryExamplePage />,
    protected: true,
    title: "Sentry Test Page",
    navbar: true,
  },
  {
    path: "*",
    element: <NotFound />,
    title: "404 Not Found",
    navbar: false,
  },
];

// Helper function to wrap protected routes
export const processRoutes = (routes: AppRoute[]): RouteObject[] => {
  return routes.map(({ protected: isProtected, adminRequired, element, title, ...rest }) => {
    let routeElement = element;

    // First check if it's an admin route
    if (adminRequired) {
      routeElement = <AdminProtectedRoute>{element}</AdminProtectedRoute>;
    }
    // If not admin but protected, use regular protected route
    else if (isProtected) {
      routeElement = <ProtectedRoute>{element}</ProtectedRoute>;
    }

    const route = {
      ...rest,
      element: routeElement,
    } as RouteObject;

    if (rest.index === true) {
      route.index = true;
    }
    return route;
  });
};
